import { z } from "zod";
import { EMAIL_RE, isPhoneComplete } from "@/shared/utils";
import type { OrganizationMe, OrganizationMeUpdate } from "./types";

export const ALLOWED_PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

export function normalizeIban(value: string): string {
  return value.replace(/[\s-]/g, "");
}

export function validatePhotoFile(file: File): string | null {
  const mime = file.type;
  if (!ALLOWED_PHOTO_MIME_TYPES.includes(mime as (typeof ALLOWED_PHOTO_MIME_TYPES)[number])) {
    return "photo.error.unsupported_format";
  }
  if (file.size > MAX_PHOTO_SIZE_BYTES) {
    return "photo.error.too_large";
  }
  return null;
}

export const organizationFormSchema = z.object({
  email: z
    .string()
    .trim()
    .refine(
      function validateEmailShape(value) {
        if (value === "") return true;
        return EMAIL_RE.test(value);
      },
      { message: "form.email.error.invalid" },
    ),
  phone: z
    .string()
    .refine(
      function validatePhoneShape(value) {
        return value.trim() === "" || isPhoneComplete(value);
      },
      { message: "form.phone.error.invalidPhone" },
    ),
  address_ru: z.string(),
  address_kk: z.string(),
  director_basis: z
    .string()
    .trim()
    .min(1, { message: "form.directorBasis.error.required" })
    .max(100, { message: "form.directorBasis.error.max" }),
  activity_type: z
    .string()
    .max(255, { message: "form.activityType.error.max" }),
  bank_name: z
    .string()
    .max(200, { message: "form.bankName.error.max" }),
  iban: z
    .string()
    .refine(
      function validateIbanLength(value) {
        return normalizeIban(value).length <= 34;
      },
      { message: "form.iban.error.max" },
    ),
  bik: z
    .string()
    .max(20, { message: "form.bik.error.max" }),
});

export type OrganizationFormValues = z.infer<typeof organizationFormSchema>;

export function formValuesFromData(data: OrganizationMe): OrganizationFormValues {
  return {
    email: data.email ?? "",
    phone: data.phone ?? "",
    address_ru: data.address_ru ?? "",
    address_kk: data.address_kk ?? "",
    director_basis: data.director_basis ?? "",
    activity_type: data.activity_type ?? "",
    bank_name: data.bank_name ?? "",
    iban: data.iban ?? "",
    bik: data.bik ?? "",
  };
}

function normalizeValue(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

type NullableField = "email" | "phone" | "address_ru" | "address_kk" | "bank_name" | "iban" | "bik";

const NULLABLE_FIELDS: NullableField[] = [
  "email",
  "phone",
  "address_ru",
  "address_kk",
  "bank_name",
  "iban",
  "bik",
];

export function diffUpdate(values: OrganizationFormValues, original: OrganizationMe): OrganizationMeUpdate {
  const patch: OrganizationMeUpdate = {};

  for (const field of NULLABLE_FIELDS) {
    const formValue = values[field];
    const originalValue = original[field];
    const normalizedForm = field === "iban" && typeof formValue === "string"
      ? normalizeValue(normalizeIban(formValue))
      : normalizeValue(formValue);
    const normalizedOriginal = field === "iban" && typeof originalValue === "string"
      ? normalizeValue(normalizeIban(originalValue))
      : normalizeValue(originalValue);
    if (normalizedForm !== normalizedOriginal) {
      patch[field] = normalizedForm;
    }
  }

  const directorBasisForm = values.director_basis.trim();
  const directorBasisOriginal = (original.director_basis ?? "").trim();
  if (directorBasisForm !== directorBasisOriginal) {
    patch.director_basis = directorBasisForm;
  }

  const activityTypeForm = values.activity_type.trim();
  const activityTypeOriginal = (original.activity_type ?? "").trim();
  if (activityTypeForm !== activityTypeOriginal) {
    patch.activity_type = activityTypeForm;
  }

  return patch;
}
